/**
 * Curved-row packing and desktop pose, generalized from the PLA-834 partner
 * icon strips (Story Time Chess crown / JRMF U) so any 1–6 tiles can use it.
 */

export const CURVED_ROW_MAX_ITEMS = 6;

/** Half-span of the circular U, from the downward vertical (JRMF). */
export const CURVED_ROW_ARC_EDGE_DEG = 18;
/** How far the center sits from the outer edges on desktop (JRMF drop / STC lift). */
export const CURVED_ROW_ARC_DROP_PX = 72;
export const CURVED_ROW_CROWN_LIFT_PX = 24;
export const CURVED_ROW_CROWN_EDGE_ROTATE_DEG = 7;
/** Middle column is wider in-flow so the flagship stays larger with even gutters. */
export const CURVED_ROW_FEATURED_TRACK = 1.5;

export type CurvedRowDirection = "up" | "down";

export interface CurvedRowPose {
  rotateDeg: number;
  translateY: number;
}

function roundPose(value: number): number {
  return Number(value.toFixed(2));
}

function clampCount(count: number): number {
  if (!Number.isFinite(count) || count < 1) return 1;
  return Math.min(CURVED_ROW_MAX_ITEMS, Math.floor(count));
}

/** Which indices get the wider desktop track when middle emphasis is on. */
export function curvedRowFeaturedIndices(count: number): number[] {
  const n = clampCount(count);
  if (n <= 1) return [0];
  if (n % 2 === 1) return [Math.floor(n / 2)];
  return [n / 2 - 1, n / 2];
}

export function isCurvedRowFeaturedIndex(index: number, count: number): boolean {
  return curvedRowFeaturedIndices(count).includes(index);
}

/**
 * Desktop pose. `down` is JRMF's circular U (origin-top). `up` is STC's crown
 * (origin-bottom): sampled lifts/rotations for 5, interpolated for other counts.
 */
export function curvedRowPose(
  index: number,
  count: number,
  direction: CurvedRowDirection
): CurvedRowPose {
  const n = clampCount(count);
  const i = Math.max(0, Math.min(n - 1, Math.floor(index)));
  if (n === 1) return { rotateDeg: 0, translateY: 0 };

  if (direction === "down") {
    const t = i / (n - 1);
    const thetaDeg = -CURVED_ROW_ARC_EDGE_DEG + t * 2 * CURVED_ROW_ARC_EDGE_DEG;
    const thetaRad = (thetaDeg * Math.PI) / 180;
    const edgeRad = (CURVED_ROW_ARC_EDGE_DEG * Math.PI) / 180;
    const radius = CURVED_ROW_ARC_DROP_PX / (1 - Math.cos(edgeRad));
    return {
      rotateDeg: roundPose(-thetaDeg),
      translateY: roundPose(radius * (Math.cos(thetaRad) - Math.cos(edgeRad))),
    };
  }

  const sampleLifts = [0, 18, CURVED_ROW_CROWN_LIFT_PX, 18, 0];
  const sampleRotates = [
    -CURVED_ROW_CROWN_EDGE_ROTATE_DEG,
    -3.5,
    0,
    3.5,
    CURVED_ROW_CROWN_EDGE_ROTATE_DEG,
  ];
  const t = i / (n - 1);
  const sampleT = t * (sampleLifts.length - 1);
  const left = Math.floor(sampleT);
  const right = Math.min(sampleLifts.length - 1, left + 1);
  const frac = sampleT - left;
  const lift = sampleLifts[left] + (sampleLifts[right] - sampleLifts[left]) * frac;
  const rotate = sampleRotates[left] + (sampleRotates[right] - sampleRotates[left]) * frac;
  return {
    rotateDeg: roundPose(rotate),
    translateY: roundPose(-lift),
  };
}

export function curvedRowTransform(
  index: number,
  count: number,
  direction: CurvedRowDirection
): string {
  const pose = curvedRowPose(index, count, direction);
  return `translateY(${pose.translateY}px) rotate(${pose.rotateDeg}deg)`;
}

/**
 * Tailwind `grid-cols-[…]` tracks for desktop. Featured tracks are 1.5fr when
 * `enlargeMiddle` is on; otherwise every track is 1fr.
 */
export function curvedRowDesktopTrackTemplate(count: number, enlargeMiddle: boolean): string {
  const n = clampCount(count);
  const featured = new Set(enlargeMiddle ? curvedRowFeaturedIndices(n) : []);
  const tracks = Array.from({ length: n }, (_, index) =>
    featured.has(index) ? `minmax(0,${CURVED_ROW_FEATURED_TRACK}fr)` : "minmax(0,1fr)"
  );
  return tracks.join("_");
}

export interface CurvedRowMobileCell {
  colSpan: number;
  colStart: number;
  rowStart: number;
}

/**
 * Mobile 6-col packing, matching PLA-834:
 * 1: full width
 * 2: two on one row
 * 3: two on top, one centered
 * 4: two / two
 * 5: two / featured centered / two (STC diamond)
 * 6: three / three
 */
export function curvedRowMobileCells(count: number): CurvedRowMobileCell[] {
  const n = clampCount(count);
  switch (n) {
    case 1:
      return [{ colSpan: 6, colStart: 1, rowStart: 1 }];
    case 2:
      return [
        { colSpan: 3, colStart: 1, rowStart: 1 },
        { colSpan: 3, colStart: 4, rowStart: 1 },
      ];
    case 3:
      return [
        { colSpan: 3, colStart: 1, rowStart: 1 },
        { colSpan: 3, colStart: 4, rowStart: 1 },
        { colSpan: 4, colStart: 2, rowStart: 2 },
      ];
    case 4:
      return [
        { colSpan: 3, colStart: 1, rowStart: 1 },
        { colSpan: 3, colStart: 4, rowStart: 1 },
        { colSpan: 3, colStart: 1, rowStart: 2 },
        { colSpan: 3, colStart: 4, rowStart: 2 },
      ];
    case 5:
      return [
        { colSpan: 3, colStart: 1, rowStart: 1 },
        { colSpan: 3, colStart: 4, rowStart: 1 },
        { colSpan: 4, colStart: 2, rowStart: 2 },
        { colSpan: 3, colStart: 1, rowStart: 3 },
        { colSpan: 3, colStart: 4, rowStart: 3 },
      ];
    default:
      return [
        { colSpan: 2, colStart: 1, rowStart: 1 },
        { colSpan: 2, colStart: 3, rowStart: 1 },
        { colSpan: 2, colStart: 5, rowStart: 1 },
        { colSpan: 2, colStart: 1, rowStart: 2 },
        { colSpan: 2, colStart: 3, rowStart: 2 },
        { colSpan: 2, colStart: 5, rowStart: 2 },
      ];
  }
}

const MOBILE_CELL_CLASS: Record<string, string> = {
  "6-1-1": "col-span-6 col-start-1 row-start-1",
  "3-1-1": "col-span-3 col-start-1 row-start-1",
  "3-4-1": "col-span-3 col-start-4 row-start-1",
  "4-2-2": "col-span-4 col-start-2 row-start-2",
  "3-1-2": "col-span-3 col-start-1 row-start-2",
  "3-4-2": "col-span-3 col-start-4 row-start-2",
  "3-1-3": "col-span-3 col-start-1 row-start-3",
  "3-4-3": "col-span-3 col-start-4 row-start-3",
  "2-1-1": "col-span-2 col-start-1 row-start-1",
  "2-3-1": "col-span-2 col-start-3 row-start-1",
  "2-5-1": "col-span-2 col-start-5 row-start-1",
  "2-1-2": "col-span-2 col-start-1 row-start-2",
  "2-3-2": "col-span-2 col-start-3 row-start-2",
  "2-5-2": "col-span-2 col-start-5 row-start-2",
};

export function curvedRowMobileCellClass(index: number, count: number): string {
  const cell = curvedRowMobileCells(count)[index];
  if (!cell) return "col-span-2";
  return MOBILE_CELL_CLASS[`${cell.colSpan}-${cell.colStart}-${cell.rowStart}`] ?? "col-span-2";
}
